<template>
    <InnerImageZoom
        id="zoomable"
        ref="zoomableRef"
        v-if="isImage && props.zoomControls.allow"
        :src="media"
        :zoomSrc="resolvedZoomSrc"
        :srcSet="resolvedSrcSet"
        :sizes="resolvedSizes"
        :sources="pictureSourcesForZoom"
        :alt="alt"
        v-bind="props.zoomControls"
        :zoomScale="zoomScaleComputed"
        @mouseover="onHover"
    />
    <video
        :style="{
            'pointer-events': !ambient ? 'all' : 'none'
        }"
        id="video"
        ref="videoRef"
        v-else-if="isVideo"
        :alt="props.alt"
        :class="props.mediaClasses"
        v-bind="videoAttrs"
    >
    <source :src="props.media" type="video/mp4"/>
    Your browser does not support videos
</video>
    <img
        id="image"
        :loading="lazy ? 'lazy' : 'eager'"
        v-else
        :src="optimizedImageUrl"
        :srcset="resolvedSrcSet"
        :sizes="resolvedSizes"
        :alt="props.alt"
        :class="props.mediaClasses"
    />
</template>
<script lang="ts" setup>
    import {
        onMounted,
        ref,
        toRefs,
        computed,
        watch,
        onBeforeUnmount,
        nextTick,
        useAttrs,
        type PropType
    } from "vue";
    import InnerImageZoom from "vue-inner-image-zoom";
    import "vue-inner-image-zoom/lib/vue-inner-image-zoom.css";
    import { useElementVisibility } from "@vueuse/core";
    import useImageOptimization from "@composables/useImageOptimization";
    import type { ZoomControls } from "@/helpers/interfaces/ZoomControls";
    import type { MediaPictureSource } from "@/helpers/mediaResponsive";
    import { normalizePictureSources } from "@/helpers/mediaResponsive";
    let controls = ref(true);
    const props = defineProps({
        media: {
            type: String,
            required: true
        },
        mediaClasses: {
            type: String,
            default: ""
        },
        alt: {
            type: String,
            default: ""
        },
        defaultMediaType: {
            type: String,
            default: ""
        },
        zoomControls: {
            type: Object as PropType<ZoomControls>,
            default() {
                return { allow: false };
            }
        },
        ambient: {
            type: Boolean,
            default: false
        },
        lazy: {
            type: Boolean,
            default: true
        },
        srcSet: {
            type: String,
            default: ""
        },
        sizes: {
            type: String,
            default: ""
        },
        sources: {
            type: Array as PropType<MediaPictureSource[]>,
            default: () => []
        },
        zoomSrc: {
            type: String,
            default: ""
        }
    });

    const resolvedSrcSet = computed(() => props.srcSet.trim() || undefined);
    const resolvedSizes = computed(() => props.sizes.trim() || undefined);
    const resolvedZoomSrc = computed(() => props.zoomSrc.trim() || undefined);
    const pictureSourcesForZoom = computed(() =>
        normalizePictureSources(props.sources)
    );

    function fetchMedia(media: string) {
        if(media.includes('https://cdn.builder.io')){
            if(media.includes('/image/assets')){
                return {
                    headers: {
                        get: (key: string) => {
                            return "image/jpeg";
                        }
                    }
                }
            }else{
                return {
                    headers: {
                        get: (key: string) => {
                            return "video/mp4";
                        }
                    }
                }
            }
        }

        return fetch(media);
    }

    const mediaType = ref("");
    const zoomableRef = ref(null);
    const zoomableWidth = ref(0);
    const zoomableHeight = ref(0);
    const zoomFactor = 2;
    let imageNaturalWidth = ref();
    let imageNaturalHeight = ref();

    if (props.defaultMediaType != "") {
        mediaType.value = props.defaultMediaType;
    }

    function onHover() {
        handleResize();
    }
    const handleResize = () => {
        nextTick(() => {
            zoomableWidth.value = zoomableRef.value.$el.offsetWidth;
            imageNaturalWidth.value =
                zoomableRef.value?.$el?.querySelector(
                    ".iiz__zoom-img"
                )?.naturalWidth;

            zoomableHeight.value = zoomableRef.value.$el.offsetHeight;
            imageNaturalHeight.value =
                zoomableRef.value?.$el?.querySelector(
                    ".iiz__zoom-img"
                )?.naturalHeight;
        });
    };
    let resizeObserver;

    onMounted(async () => {
        resizeObserver = new ResizeObserver(handleResize);
        if (props.defaultMediaType != "") {
            return;
        }
        const response = await fetchMedia(props.media);
        mediaType.value = response.headers.get("Content-Type") ?? "";
    });

    onBeforeUnmount(() => {
        resizeObserver.disconnect();
    });

    let zoomScaleComputed = computed(() => {
        if(+imageNaturalHeight.value > +imageNaturalWidth.value){
            return +zoomableHeight.value && +imageNaturalHeight.value
                ? (+zoomableHeight.value * zoomFactor) / +imageNaturalHeight.value
                : zoomFactor;
        }else{
            return +zoomableWidth.value && +imageNaturalWidth.value
                ? (+zoomableWidth.value * zoomFactor) / +imageNaturalWidth.value
                : zoomFactor;
        }
        
    });

    let isImage = computed(() => mediaType.value.includes("image"));
    let isVideo = computed(() => mediaType.value.includes("video"));

    let video = defineModel('video', {
        type: Boolean,
        default: false
    })
    watch(mediaType, (newVal) => {
        if (props.zoomControls.allow && isImage && zoomableRef.value) {
            resizeObserver.observe(zoomableRef.value);
        }
    });


    watch(isVideo, (newVal) => {
        // if (newVal) {
        video.value = newVal;
        // }
    });


    let $attrs = useAttrs();
    let videoRef = ref();
    let videoAttrs = computed(()=>{
        // grab attributes related to videos from $attrs
        let attrs = {
            autoplay: true,
            loop: true,
            muted: false,
            playsinline: true,
            volume: 0.5
        }

     
        for(let attr in $attrs){
            if(['autoplay', 'muted', 'loop', 'playsinline'].includes(attr)){
                attrs[attr] = $attrs[attr]
            }
        }
        return attrs;
    })


    function showControls() {
        controls.value = true;
    }

    function hideControls() {
        console.log("hiding controls");
        controls.value = false;
    }


    const isVisible = useElementVisibility(videoRef);

    watch(isVisible, (newVal) => {
        videoRef.value[newVal ? "play" : "pause"]();

    }, { immediate: false });

    const { media } = toRefs(props);
    let { optimizedImageUrl } = useImageOptimization(media);
</script>

<style>
    .iiz {
        display: block !important;
    }
    .iiz__img {
        width: 100% !important;
    }

    /* #video {
        display:block;
    } */
</style>
